import { debounce } from './utils/debounce';
import { de } from './translations';

const SEARCH_CATEGORY = 'search';

/**
 * Enqueue a Matomo command.
 * @param {Array} command The command to push.
 */
const matomoCommand = (command = []) => {
    window._paq = window._paq || [];
    window._paq.push(command);
};

/**
 * Track an event in Matomo.
 * @param {string} category Event category.
 * @param {string} action   Event action.
 * @param {string} name     Event name.
 */
const trackEvent = (category, action, name) => {
    matomoCommand(['trackEvent', category, action, name]);
};

/**
 * Track a site search in Matomo.
 * Only fires for queries of 3 or more characters.
 * @param {string} query         The search query.
 * @param {number|false} resultsCount  Number of results, or false if unknown.
 */
const trackSearchQuery = (query, resultsCount = false) => {
    if (!query || query.length < 3) return;
    matomoCommand(['trackSiteSearch', query, SEARCH_CATEGORY, resultsCount]);
};

/**
 * Return only the facet filters that were newly added (not removed) between
 * two InstantSearch UI states. Covers refinementList and menu widgets.
 *
 * @param {object} previousState
 * @param {object} newState
 * @returns {object} Map of attribute → newly added values.
 */
const getAddedFilters = (previousState, newState) => {
    const previousFacets = {
        ...(previousState.refinementList || {}),
        ...(previousState.menu || {}),
    };
    const newFacets = {
        ...(newState.refinementList || {}),
        ...(newState.menu || {}),
    };

    const getNewValues = (prevValues, newValues) => {
        if (Array.isArray(newValues)) {
            return newValues.filter(v => !(prevValues || []).includes(v));
        }
        if (typeof newValues === 'string') {
            return prevValues !== newValues ? [newValues] : [];
        }
        return [];
    };

    return Object.fromEntries(
        Object.entries(newFacets)
            .map(([attr, values]) => [attr, getNewValues(previousFacets[attr], values)])
            .filter(([, values]) => values.length > 0),
    );
};

/**
 * InstantSearch middleware factory for Matomo event tracking.
 *
 * Tracks:
 * - Search queries (debounced, min. 3 chars, deduped against URL-param initial query)
 * - Applied facet filters
 * - Sort order changes
 * - Clicks on search results (with immediate query attribution)
 *
 * @param {string} indexName     The MeiliSearch index name used as the InstantSearch index key.
 * @param {object} initialState  The initial UI state (used as baseline to avoid tracking URL-param queries).
 */
export const eventTrackingMiddlewareFactory =
    (indexName, initialState = {}) =>
    ({ instantSearchInstance }) => {
        let previousState = {};
        let lastTrackedQuery = null;

        const trackQueryDebounced = debounce(query => {
            if (query === lastTrackedQuery) return;
            lastTrackedQuery = query;
            trackSearchQuery(query, instantSearchInstance.helper?.lastResults?.nbHits ?? false);
        }, 2000);

        const trackQueryImmediate = query => {
            if (query === lastTrackedQuery) return;
            lastTrackedQuery = query;
            trackSearchQuery(query, instantSearchInstance.helper?.lastResults?.nbHits ?? false);
        };

        let clickHandler = null;

        return {
            onStateChange({ uiState }) {
                const state = uiState[indexName];
                if (!state) return;

                // Track search query (debounced, skip if the new query is a prefix
                // of what the user already typed – e.g. mid-backspace)
                const hasNewQuery =
                    state.query &&
                    state.query !== previousState.query &&
                    !(previousState.query || '').includes(state.query);
                if (hasNewQuery) {
                    trackQueryDebounced(state.query);
                }

                // Track newly applied facet filters
                const addedFilters = getAddedFilters(previousState, state);
                Object.entries(addedFilters).forEach(([attr, values]) => {
                    trackEvent(SEARCH_CATEGORY, 'filter_used', attr);
                    values.forEach(value =>
                        trackEvent(
                            SEARCH_CATEGORY,
                            'facet_added',
                            de[`${attr}.${value}`] ?? de[value] ?? value,
                        ),
                    );
                });

                // Track sort order changes (ignore resets to the default sort)
                if (state.sortBy && state.sortBy !== previousState.sortBy) {
                    trackEvent(SEARCH_CATEGORY, 'sort_changed', state.sortBy);
                }

                previousState = state;
            },

            subscribe() {
                // Treat the initial state as the baseline so we don't fire a
                // query event for a pre-filled ?q= URL parameter.
                previousState = initialState;

                const hitsContainer = document.querySelector('[data-widget="infiniteHits"]');
                if (!hitsContainer) return;

                // Track clicks on result links. If an active query exists, also
                // fire it immediately to attribute the query to the click, bypassing
                // the debounce.
                clickHandler = e => {
                    const link = e.target.closest('a');
                    if (!link) return;
                    const linkText = link.textContent.trim();
                    if (linkText) {
                        trackEvent(SEARCH_CATEGORY, 'result_clicked', linkText);
                    }
                    const currentQuery = instantSearchInstance.helper?.state?.query;
                    if (currentQuery) {
                        trackQueryImmediate(currentQuery);
                    }
                };

                hitsContainer.addEventListener('click', clickHandler);
            },

            unsubscribe() {
                const hitsContainer = document.querySelector('[data-widget="infiniteHits"]');
                if (hitsContainer && clickHandler) {
                    hitsContainer.removeEventListener('click', clickHandler);
                }
                clickHandler = null;
            },
        };
    };
